import { useNavigate } from "react-router-dom";
import { NavigationItem } from "./navigationData";
import CardBase from "./card/CardBase";
import CardTitle from "./card/CardTitle";
import { useMediaQuery } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { Lock } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface NavigationCardProps {
  item: NavigationItem;
  pendingRequestsCount?: number;
  supportUnreadCount?: number;
  /** Generic attention dot (e.g. Mina objekt – kontrakt att signera). */
  showAttention?: boolean;
}

const NavigationCard = ({
  item,
  pendingRequestsCount = 0,
  supportUnreadCount = 0,
  showAttention = false,
}: NavigationCardProps) => {
  const navigate = useNavigate();
  const { isNative } = useNativePlatform();
  const Icon = item.icon;
  // Extra small is < 375px (not 480px) to differentiate from normal mobile (425px)
  const isExtraSmall = useMediaQuery("(max-width: 374px)");

  // Extract border color from gradient classes for icon color (same as CardBase glow)
  const getIconColor = (colorClass: string): string => {
    const hexMatch = colorClass.match(/from-\[#([A-Fa-f0-9]{6})\]/);
    if (hexMatch) {
      return `#${hexMatch[1]}`;
    }
    return "#3b82f6"; // fallback
  };

  const iconColor = getIconColor(item.color);

  const handleClick = () => {
    if (!item.disabled) {
      navigate(item.path, isNative ? { state: { nativeSlideIn: true } } : undefined);
    }
  };

  const cardContent = (
    <CardBase
      onClick={handleClick}
      color={item.color}
      animation={item.animation}
      disabled={item.disabled}
    >
      <div className="">
        <div
          className={`absolute ${
            isExtraSmall ? "top-1.5 left-1.5" : "top-2 left-2"
          }`}
        >
          <Icon
            className={isExtraSmall ? "h-3 w-3" : "h-4 md:h-5 w-4 md:w-5"}
            style={{ color: iconColor }}
          />
        </div>
        {item.disabled && (
          <div
            className={`absolute ${
              isExtraSmall ? "top-1.5 right-1.5" : "top-2 right-2"
            } z-20`}
          >
            <div
              className={`rounded-full bg-white/90 dark:bg-black/60 backdrop-blur-sm border border-gray-300 dark:border-gray-600 shadow-sm ${
                isExtraSmall ? "p-1" : "md:p-1.5 p-1"
              }`}
            >
              <Lock
                className={`${
                  isExtraSmall ? "h-3 w-3" : "h-3 md:h-4 w-3 md:w-4"
                } text-gray-600 dark:text-gray-400`}
              />
            </div>
          </div>
        )}
        {(pendingRequestsCount > 0 && item.path === "/settings") ||
        (supportUnreadCount > 0 &&
          (item.path === "/support" || item.path === "/admin/support")) ||
        showAttention ? (
          <div
            className={`absolute ${
              isExtraSmall ? "top-2 right-2" : "top-3 right-3"
            } z-20`}
          >
            <div
              className={`rounded-full bg-red-500 border-2 border-white dark:border-gray-900 shadow-sm ${
                isExtraSmall ? "h-2 w-2" : "h-3 w-3"
              }`}
            />
          </div>
        ) : null}
        <div className={isExtraSmall ? "pt-4" : "pt-5"}>
          <CardTitle title={item.title} subtitle={item.subtitle} />
        </div>
      </div>
    </CardBase>
  );

  if (item.disabled && item.disabledTooltip) {
    return (
      <div className={item.width}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="cursor-not-allowed">{cardContent}</div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">{item.disabledTooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return <div className={item.width}>{cardContent}</div>;
};

export default NavigationCard;
